import React from 'react';
import { useNavigate } from 'react-router-dom';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import { Box, Popover } from '@mui/material';
import { Form, FormRenderProps } from 'react-final-form';

import {
  Button,
  TableContainer,
  TableHeader,
  Typography,
  FilterButton,
  isFiltersSelected,
} from '@occc/ui-design-system';
import { snackbar, Select } from '@occc-app/design-system';
import { deletePaginationParams, usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetWallets } from '@occc-app/dataAccessLayer/steeringWallet/queries';
import { SteeringWalletFragment } from '@occc/graphql';
import { useSteeringWalletArchive } from '@occc-app/dataAccessLayer/steeringWallet/mutations';
import { useChainId } from '@occc-app/Chain/hooks';
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { WalletsTable } from './WalletsTable';

import { messages } from '../messages';
import { routeCreator } from '../utils';
import { STATUS_PARAM, WALLETS_STATUS_ALL, statusFilterOptions } from '../constants';
import { WalletsFilterFormValues, WalletsStatusType } from '../types';
import { ArchiveWalletConfirmDialog } from '../components/ArchiveWalletConfirmDialog';

export const WalletsTableContainer: React.FC = () => {
  const navigate = useNavigate();
  const chainId = useChainId();

  const filterPopupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const isUserPermittedToEditWallet = useIsUserPermittedTo(Feature.WALLET_EDIT);
  const isUserPermittedToViewWalletConfiguration = useIsUserPermittedTo(
    Feature.WALLET_EDIT_VIEW_ONLY,
  );
  const isUserPermittedToArchiveWallet = useIsUserPermittedTo(Feature.WALLET_ARCHIVE);

  const [walletToArchive, setWalletToArchive] = React.useState<null | SteeringWalletFragment>(null);

  const [status, setStatus] = useQueryParam<WalletsStatusType>(
    (searchParams) => {
      const status = searchParams.get(STATUS_PARAM) ?? WALLETS_STATUS_ALL;
      return status as WalletsStatusType;
    },
    (value, searchParams) => {
      if (value && value !== WALLETS_STATUS_ALL) {
        searchParams.set(STATUS_PARAM, value);
      } else {
        searchParams.delete(STATUS_PARAM);
      }

      deletePaginationParams(searchParams);
    },
  );

  const [paginationInfo, onPageChange] = usePaginationParams();
  const { loading, error, data, paginationProps } = useGetWallets({
    paginationInfo,
    onPageChange,
    baseOptions: {
      variables: {
        filter: {
          statusEquals: status !== WALLETS_STATUS_ALL ? status : undefined,
          chainIdEquals: chainId,
        },
      },
    },
  });

  const handleCloseArchiveConfirmationDialog = React.useCallback(() => {
    setWalletToArchive(null);
  }, []);

  const onArchiveError = React.useCallback(() => {
    snackbar.error(messages.archiveWalletError);
  }, []);

  const onArchiveCompleted = React.useCallback(() => {
    snackbar.success(messages.archiveWalletSuccess);
    handleCloseArchiveConfirmationDialog();
  }, [handleCloseArchiveConfirmationDialog]);

  const [archiveWallet, { loading: archiveLoading }] = useSteeringWalletArchive({
    onCompleted: onArchiveCompleted,
    onError: onArchiveError,
    refetchQueries: ['steeringWallets'],
  });

  const onArchiveWallet = async () => {
    if (!walletToArchive) {
      return;
    }

    archiveWallet({
      variables: {
        uuid: walletToArchive.uuid,
      },
    });
  };

  const onViewDetails = React.useCallback(
    (item: SteeringWalletFragment) => {
      navigate(routeCreator.details(item.uuid));
    },
    [navigate],
  );

  const onEdit = React.useCallback(
    (item: SteeringWalletFragment) => {
      navigate(routeCreator.update(item.uuid));
    },
    [navigate],
  );

  const filterPopoverProps = React.useMemo(() => {
    return {
      ...bindPopover({
        ...filterPopupState,
      }),
    };
  }, [filterPopupState]);

  const renderActions = () => {
    return (
      <FilterButton
        {...bindTrigger(filterPopupState)}
        ref={anchorRef(filterPopupState)}
        showBadge={isFiltersSelected([status], [WALLETS_STATUS_ALL])}
      />
    );
  };

  const onFilterFormSubmit = (values: WalletsFilterFormValues) => {
    setStatus(values.status);
    filterPopoverProps.onClose();
  };

  const onFilterFormReset = React.useCallback(() => {
    setStatus(undefined);
    filterPopoverProps.onClose();
  }, [filterPopoverProps, setStatus]);

  const renderFilterForm: FormRenderProps<WalletsFilterFormValues>['render'] = React.useCallback(
    ({ handleSubmit, form }: FormRenderProps<WalletsFilterFormValues>) => {
      const reset = () => {
        form.reset();
        onFilterFormReset();
      };

      return (
        <form onSubmit={handleSubmit}>
          <Box px={4} pt={4}>
            <Typography paragraph>Status</Typography>
            <Select name='status' label='Status' data={statusFilterOptions} />
          </Box>
          <Box display='flex' justifyContent='flex-end' px={3} py={2}>
            <Button size='small' color='error' onClick={reset}>
              reset
            </Button>
            <Button type='submit' size='small' color='primary'>
              apply
            </Button>
          </Box>
        </form>
      );
    },
    [onFilterFormReset],
  );

  return (
    <>
      <TableContainer>
        <TableHeader title={messages.title} actions={renderActions()} />
        <WalletsTable
          wallets={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          isUserPermittedToEditWallet={isUserPermittedToEditWallet}
          isUserPermittedToArchiveWallet={isUserPermittedToArchiveWallet}
          isUserPermittedToViewWalletConfiguration={isUserPermittedToViewWalletConfiguration}
          onEdit={onEdit}
          onViewDetails={onViewDetails}
          onArchive={setWalletToArchive}
        />
        <Popover
          {...filterPopoverProps}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          keepMounted
          PaperProps={{ sx: { width: 300 } }}
        >
          <Form<WalletsFilterFormValues>
            render={renderFilterForm}
            onSubmit={onFilterFormSubmit}
            initialValues={{ status }}
          />
        </Popover>
      </TableContainer>

      {walletToArchive && (
        <ArchiveWalletConfirmDialog
          onClose={handleCloseArchiveConfirmationDialog}
          onConfirm={onArchiveWallet}
          loading={archiveLoading}
          walletName={walletToArchive.name}
        />
      )}
    </>
  );
};
